<template>
  <modal-window
    v-if="modal_open"
    :is-closable="!loading"
    :modal_open="showModal"
    :max-width="550"
    title="Make an enquiry and save your visualisation"
    @close="$emit('close')"
  >
    <loading :loading="loading">
      <div class="">
        <div>Please complete the simple form below.</div>
        <div class="mt-3">
          <div class="flex -mx-2">
            <div class="mb-3 mx-2 flex-1">
              <BaseInput
                v-model="firstName"
                label="First Name"
                type="text"
                :required="true"
                :class="{ 'border border-red-500': fieldErrors.firstName }"
              />
            </div>
            <div class="mb-3 mx-2 flex-1">
              <BaseInput
                v-model="lastName"
                label="Last Name"
                type="text"
                :required="true"
                :class="{ 'border border-red-500': fieldErrors.lastName }"
              />
            </div>
          </div>

          <div class="flex flex-col -mx-2">
            <div class="flex mb-3 px-2 w-full">
              <div class="grow">
                <BaseInput
                  v-model="email"
                  label="Email"
                  type="email"
                  :required="true"
                  :class="{ 'border border-red-500': fieldErrors.email }"
                />
              </div>
            </div>
            <div class="flex mb-3 px-2 w-full">
              <div class="grow">
                <BaseInput v-model="phone" label="Phone" type="number" min="0" :required="true" />
              </div>
            </div>
          </div>
          <AddressForm :is-primary-toggle="false" :field-errors-props="fieldErrors" @address-updated="addressUpdated" />
          <TextArea
            v-model="notes"
            label="Enter any notes here"
            maxlength="255"
            rows="3"
            :class="{ 'border border-red-500': fieldErrors.notes }"
          />
          <p v-if="notes.length > 0">{{ notes.length }} / 255</p>
        </div>
        <div class="flex mt-3 p-3 rounded" :class="{ 'border border-red-500': fieldErrors.acceptTerms }">
          <div>
            <input v-model="acceptTerms" type="checkbox" />
          </div>
          <div class="ml-4 text-xs text-gray-600">
            Tick the box to confirm that you agree for the information you have provided to be passed to us, we will
            contact you to discuss your items.
            <template v-if="privacyPolicyDocumentUrl != ''">
              <a
                class="text-green-600 hover:text-green-800 cursor-pointer"
                :href="privacyPolicyDocumentUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here</a
              >
              for more information about how we will use your personal information.
            </template>
            <template v-if="termsDocumentUrl != ''">
              <a
                class="text-green-600 hover:text-green-800 cursor-pointer"
                :href="termsDocumentUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Click here</a
              >
              to view our terms and conditions.
            </template>
          </div>
        </div>
        <div class="mt-3">
          <button class="btn btn-action btn-lg w-full" @click="submitEnquiry()">Submit Enquiry</button>
        </div>
        <div>
          <div class="mb-4 mt-10">
            <strong class="text-xl"> What happens next? </strong>
            <i class="fal fa-envelope" />
          </div>
          <div class="mb-3">
            You will receive an instant email containing a summary of your enquiry and visualisation of your home.
          </div>
          <div v-if="authStore.isDealerNetworkMode">One of our trusted dealers will contact you shortly.</div>
          <div v-else>A member of our team will contact you shortly.</div>
        </div>
      </div>
    </loading>
  </modal-window>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/pinia/auth'
import { useBasketStore } from '@/pinia/basket'
import { useBrandedStore } from '@/pinia/branded'
import { useVisualiserStore } from '@/pinia/visualiser'
import { useTouchStore } from '@/pinia/touch'
import AddressForm from '@/components/shared/address/AddressForm.vue'
import TextArea from '@/components/shared/ui/TextArea.vue'
import { useValidation, validators } from '@/composables/validation'

const validationRules = {
  firstName: [validators.required('labels.firstName'), validators.maxLength(32, 'labels.firstName')],
  lastName: [validators.required('labels.lastName'), validators.maxLength(32, 'labels.lastName')],
  email: [
    validators.required('labels.email'),
    validators.email('labels.email'),
    validators.maxLength(255, 'labels.email')
  ],
  phone: [validators.required('labels.phone'), validators.maxLength(64, 'labels.phone')],
  notes: [validators.maxLength(255, 'labels.notes')],
  addressLine1: [
    validators.required('delivery.labels.address1'),
    validators.maxLength(128, 'delivery.labels.address1')
  ],
  addressCode: [validators.required('delivery.labels.postcode'), validators.maxLength(16, 'delivery.labels.postcode')],
  addressIso: [validators.required('delivery.labels.country'), validators.maxLength(3, 'delivery.labels.country')],
  acceptTerms: [validators.custom((value) => value === true, 'validations.staff.termsAndConditions')]
}

export default {
  components: {
    AddressForm,
    TextArea
  },
  setup() {
    const { fieldErrors, validate } = useValidation(validationRules)
    return {
      fieldErrors,
      validate
    }
  },
  props: ['showModal'],
  data() {
    return {
      vendor_version: true,
      firstName: '',
      lastName: '',
      addressLine1: '',
      addressLine2: '',
      addressLine3: '',
      addressTown: '',
      addressCounty: '',
      addressCode: '',
      addressIso: 'GB',
      latitude: '',
      longitude: '',
      email: '',
      phone: '',
      notes: '',
      acceptTerms: false,
      termsDocumentUrl: 'http://undefined',
      privacyPolicyDocumentUrl: 'http://undefined',
      loading: false,
      manualAddressEntry: false,
      address_picked: false,
      modal_open: false
    }
  },
  computed: {
    ...mapStores(useAuthStore, useBasketStore, useVisualiserStore, useTouchStore, useBrandedStore),
    formatted_address() {
      return [
        this.addressLine1,
        this.addressLine2,
        this.addressLine3,
        this.addressTown,
        this.addressCounty,
        this.addressCode
      ]
        .filter((val) => val)
        .join(', ')
    }
  },
  watch: {
    showModal(show) {
      if (show) {
        this.visualiserStore.deactivateAllImages()
        this.loadTerms()
      }
    },
    addressIso(val) {
      if (val !== 'GB') {
        this.manualAddressEntry = true
      }
    }
  },
  async mounted() {
    if (this.showModal) {
      await this.loadTerms()
    }
    this.modal_open = true
  },
  methods: {
    async loadTerms() {
      const documents = await this.touchStore.getDocsToAccept()

      if (documents) {
        const terms = documents.filter((document) => document.type === 1)[0]
        const privacyPolicy = documents.filter((document) => document.type === 2)[0]

        if (terms) {
          this.termsDocumentUrl = terms.url
          if (!this.termsDocumentUrl.includes('//')) {
            this.termsDocumentUrl = `http://${this.termsDocumentUrl}`
          }
        }

        if (privacyPolicy) {
          this.privacyPolicyDocumentUrl = privacyPolicy.url
          if (!this.privacyPolicyDocumentUrl.includes('//')) {
            this.privacyPolicyDocumentUrl = `http://${this.privacyPolicyDocumentUrl}`
          }
        }
      }
    },
    addressUpdated(newAddress) {
      this.addressLine1 = newAddress.addressLine1
      this.addressLine2 = newAddress.addressLine2
      this.addressLine3 = newAddress.addressLine3
      this.addressTown = newAddress.addressTown
      this.addressCounty = newAddress.addressCounty
      this.addressCode = newAddress.addressCode
      this.addressIso = newAddress.addressIso
      this.latitude = newAddress.latitude
      this.longitude = newAddress.longitude
    },
    async saveQuote({
      firstName,
      lastName,
      email,
      phone,
      addressLine1,
      addressLine2,
      addressLine3,
      addressTown,
      addressCode,
      addressIso,
      notes,
      latitude,
      longitude
    }) {
      this.saving_quote = true
      this.loading = true
      await this.basketStore.saveBrandedEnquiry({
        firstName,
        lastName,
        email,
        phone,
        addressLine1,
        addressLine2,
        addressLine3,
        addressTown,
        addressCode,
        addressIso,
        notes,
        latitude,
        longitude
      })
      this.loading = false
      this.$router.replace(this.brandedStore.thanksURL)
    },
    pickedAddress(address) {
      this.addressLine1 = address.address1
      this.addressLine2 = address.address2
      this.addressLine3 = address.address3
      this.addressTown = address.town
      this.addressCounty = address.county
      this.addressCode = address.postcode
      this.latitude = address.latitude
      this.longitude = address.longitude
      this.address_picked = true
    },
    clearAddress() {
      this.addressLine1 = ''
      this.addressLine2 = ''
      this.addressLine3 = ''
      this.addressTown = ''
      this.addressCounty = ''
      this.addressCode = ''
      this.latitude = undefined
      this.longitude = undefined
      this.address_picked = false
    },
    async submitEnquiry() {
      const errors = await this.validate({
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.phone,
        notes: this.notes,
        addressLine1: this.addressLine1,
        addressCode: this.addressCode,
        addressIso: this.addressIso,
        acceptTerms: this.acceptTerms
      })

      if (errors) return

      this.loading = true
      await this.visualiserStore.saveToServer()
      await this.saveQuote({
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.phone,
        notes: this.notes,
        addressLine1: this.addressLine1,
        addressLine2: this.addressLine2,
        addressLine3: this.addressLine3,
        addressTown: this.addressTown,
        addressCounty: this.addressCounty,
        addressCode: this.addressCode,
        addressIso: this.addressIso,
        latitude: this.latitude,
        longitude: this.longitude
      })
    }
  }
}
</script>
